import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Button as ShadcnButton } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

/**
 * COMPATIBILITY SHIM — legacy Button API over shadcn's.
 *
 * The prop surface is unchanged so all 61 call sites keep working untouched.
 * shadcn's Button has no `loading`, `leftIcon`, `rightIcon` or `fullWidth`;
 * this composes them from Spinner + data-icon + a width class. Deleted in the
 * de-shim phase, where call sites move to the shadcn API directly.
 */

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  children?: ReactNode;
}

const VARIANT = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  ghost: 'ghost',
} as const;

const SIZE = {
  sm: 'sm',
  md: 'default',
  lg: 'lg',
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    children,
    disabled,
    className,
    ...rest
  },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant={VARIANT[variant]}
      size={SIZE[size]}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? <Spinner data-icon="inline-start" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </ShadcnButton>
  );
});
